import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useResetMfaForMember } from 'api/MembersAPI';
import { Modal, ModalHeader, ModalBody, ModalFooter, MODAL_VARIANTS } from 'ui/modal';
import { colors, Body2, BoldText } from 'ui/theme';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { addPositiveToast, addNegativeToastWithErrorId } from 'modules/toastContainer';
import {
  MemberPayload,
  useOrganizationModalsContext,
} from '../../contexts/OrganizationModalsContext';
import { fullName } from '../../utils';

const Body2Light = styled(Body2)`
  color: ${colors.textSecondary};
`;

type ResetMfaResponse = {
  mfaGoogleReset: boolean;
  mfaUserPassReset: boolean;
};

export const createSuccessMessage = (response: ResetMfaResponse, memberFullName: string) => {
  if (response.mfaGoogleReset && response.mfaUserPassReset) {
    return `Google MFA and MFA for ${memberFullName} were reset successfully.`;
  } else if (response.mfaGoogleReset) {
    return `Google MFA for ${memberFullName} was reset successfully.`;
  } else if (response.mfaUserPassReset) {
    return `MFA for ${memberFullName} was reset successfully.`;
  }
  /** In a standard failure case, BE returns an object with both properties
   * mfaGoogleReset and mfaUserPassReset as false. Return undefined in that case. */
};

export const ResetMultifactorModal = () => {
  const { modalState, hideModal } = useOrganizationModalsContext();
  const dispatch = useDispatch();
  const { member } = modalState?.payload as MemberPayload;

  const memberFullName = fullName(member);

  const { mutate, isLoading } = useResetMfaForMember({
    onSuccess: (response: ResetMfaResponse) => {
      const successMessage = createSuccessMessage(response, memberFullName);
      if (successMessage) {
        dispatch(addPositiveToast(`MFA for ${memberFullName} was reset successfully.`));
      } else {
        const msg = `There was an error reseting MFA for ${memberFullName}. Please try again.`;
        dispatch(
          addNegativeToastWithErrorId(msg, 'resetMultifactor', new Error(response.toString())),
        );
      }
      hideModal();
    },
    onError(error) {
      const msg = `There was an error reseting MFA for ${memberFullName}. Please try again.`;
      dispatch(addNegativeToastWithErrorId(msg, 'resetMultifactor', error));
      hideModal();
    },
  });

  const onSubmit = () => {
    mutate({ memberEmail: member.email });
  };

  return (
    <Modal
      id="reset-multifactor-modal"
      isModalOpen
      onClose={hideModal}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader>Reset MFA?</ModalHeader>
      <ModalBody>
        <Body2Light>
          Are you sure you want to reset multi-factor authentication (MFA) for{' '}
          <BoldText>
            {memberFullName} ({member.email})
          </BoldText>
          ?
        </Body2Light>
        <Body2Light>
          The next time they log in, they&apos;ll need to set up their MFA like a new user.
        </Body2Light>
      </ModalBody>
      <ModalFooter onClose={hideModal} hasCancel>
        <PrimaryButton
          onClick={onSubmit}
          size={BUTTON_SIZES.LARGE}
          isLoading={isLoading}
          isDisabled={isLoading}
        >
          Reset MFA
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
